import { EMPTY_PRODUCT_IMAGE_PATH } from '../../core/services/products.service';
import { IProductPacking } from '../../product/models/product-card';
import { DefaultProductParams } from '../../core/models/defaultProductParams';
import { Utils } from '../../core/services/utils.service';

interface IProductBaseParams {
	id: number;
	name: string;
	width: number;
	height: number;
	depth: number;
	photo: string;
	brands: Array<IProperty>;
	categories: Array<Array<ITreeProperty>>;
	article: string;
	barcode?: string;
	suppliers: Array<IProperty>;
	markers: Array<Array<ITreeProperty>>;
	price?: string;
	width_compression_coef?: number;
	depth_compression_coef?: number;
	height_compression_coef?: number;
}

export interface IProperty {
	id: number;
	name: string;
}

export interface ITreeProperty extends IProperty {
	level: number;
	parent_id?: number;
}

export class ProductBase {
	id: number;
	name: string;
	width: number;
	height: number;
	depth: number;
	price: string = '';
	photo: string = EMPTY_PRODUCT_IMAGE_PATH;
	brands: Array<IProperty> = [];
	categories: Array<Array<ITreeProperty>> = [];
	suppliers: Array<IProperty> = [];
	markers: Array<Array<ITreeProperty>>;
	article: string = null;
	barcode: string = '';
	similarProducts: ProductBase[] = [];
	width_compression_coef: number = 0;
	depth_compression_coef: number = 0;
	height_compression_coef: number = 0;
	compression: number = 0;
	image: HTMLImageElement;
	is_master_position: boolean;

	constructor(params: IProductBaseParams) {
		this.id = params.id;
		this.name = params.name;

		const defaultParams: DefaultProductParams = new DefaultProductParams();
		this.width = defaultParams.width;
		this.height = defaultParams.height;
		this.depth = defaultParams.depth;

		if (params.width) this.width = params.width;
		if (params.height) this.height = params.height;
		if (params.depth) this.depth = params.depth;
		if (params.barcode) this.barcode = params.barcode;
		if (params.price) this.price = params.price;
		if (params.width_compression_coef) this.width_compression_coef = params.width_compression_coef;
		if (params.depth_compression_coef) this.depth_compression_coef = params.depth_compression_coef;
		if (params.height_compression_coef) this.height_compression_coef = params.height_compression_coef;
		if (params.photo) this.photo = params.photo;

		this.brands = params.brands ?? [];
		this.categories = params.categories ?? [];
		this.suppliers = params.suppliers ?? [];
		this.markers = params.markers ?? [];
		this.article = params.article;
	}

	get avgPrice(): number {
		if (typeof this.price === 'number') return this.price;
		const prices: Array<number> = this.price?.split('-').map((value: string) => +value);
		if (!prices?.length) return 0;
		return Utils.sum(prices) / prices.length;
	}

	get brandsUniqueId(): string {
		return Utils.unique<number>(this.brands.map((brand: IProperty) => brand.id))
			.sort((a: number, b: number) => a - b)
			.join('-') || '-1';
	}

	get brandsUniqueName(): string {
		return Utils.unique<string>(this.brands.map((brand: IProperty) => brand.name)).sort().join('; ');
	}

	get categoriesUniqueId(): string {
		return this.categoriesUniqueIds[this.categoriesUniqueIds.length - 1] || '-1';
	}

	get categoriesUniqueIds(): Array<string> {
		const levelsMap = new Map<number, Set<number>>();
		const maxLevel: number = Math.max(
			...Utils.to1DArray(this.categories).map((c: ITreeProperty) => c.level)
		);

		for (let i = 1; i < maxLevel; i++) {
			levelsMap.set(i, new Set<number>());
			this.categories
				.map((group: Array<ITreeProperty>) => group.slice(1))
				.filter((group: Array<ITreeProperty>) => group.length)
				.forEach((group: Array<ITreeProperty>)=> {
					const maxGroupLevel: number = Math.max(...group.map((i: ITreeProperty) => i.level));
					const levelItem: ITreeProperty = group.find((item: ITreeProperty) => item.level - 1 === i || item.level === maxGroupLevel);
					levelsMap.get(i).add(levelItem.id);
				});
		}

		return [...levelsMap.keys()]
			.sort((a, b) => a - b)
			.map((level: number) => [...levelsMap.get(level)].sort((a, b) => a - b).join('-'));
	}


	get categoriesUniqueName(): string {
		return this.categoriesUniqueNames[this.categoriesUniqueNames.length - 1];
	}

	get categoriesUniqueNames(): Array<string> {
		const levelsMap = new Map<number, Set<string>>();
		const maxLevel: number = Math.max(
			...Utils.to1DArray(this.categories).map((c: ITreeProperty) => c.level)
		);

		for (let i = 1; i < maxLevel; i++) {
			levelsMap.set(i, new Set<string>());
			this.categories
				.map((group: Array<ITreeProperty>) => group.slice(1))
				.filter((group: Array<ITreeProperty>) => group.length)
				.forEach((group: Array<ITreeProperty>)=> {
					const maxGroupLevel: number = Math.max(...group.map((i: ITreeProperty) => i.level));
					const levelItem: ITreeProperty = group.find((item: ITreeProperty) => item.level - 1 === i || item.level === maxGroupLevel);
					levelsMap.get(i).add(levelItem.name);
				});
		}

		return [...levelsMap.keys()]
			.sort((a, b) => a - b)
			.map((level: number) => [...levelsMap.get(level)].sort().join('; '));
	}

	get suppliersUniqueId(): string {
		return Utils.unique<number>(this.suppliers.map((supplier: IProperty) => supplier.id))
			.sort((a: number, b: number) => a - b)
			.join('-') || '-1';
	}

	get suppliersUniqueName(): string {
		return Utils.unique<string>(this.suppliers.map((supplier: IProperty) => supplier.name)).sort().join('; ');
	}

	update(obj: any) {
		for (let key in obj) {
			if (obj.hasOwnProperty(key)) {
				this[key] = obj[key];
				if (key === 'photo') {
					if (this.image) this.image.src = this.photo || EMPTY_PRODUCT_IMAGE_PATH;
				}
				if (key === 'packings') {
					this[key].forEach((packing: IProductPacking) => {
						packing.image = new Image();
						packing.image.crossOrigin = 'anonymous';
						packing.image.onerror = () => {
							packing.image.onerror = null;
							packing.image.src = EMPTY_PRODUCT_IMAGE_PATH;
							return true;
						};
						packing.image.src = packing.photo || EMPTY_PRODUCT_IMAGE_PATH;
					});
				}
			}
		}
	}

	nameWithArticle(mpTitle?: string): string {
		let value: string = this.name;
		if (this.article) value = `${this.article} | ${value}`;
		if (this.is_master_position && mpTitle) value = `${mpTitle} | ${value}`;
		return value;
	}
}